import { RiveByKey } from "@components/riveAnimation";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useModals } from "../../../context/ModalContext";
import { sanitizeSearchQuery } from "../../../utils/sanitizeSearchQuery";
import { BaseModalComponent } from "../../common/BaseModalComponent";
import { JobMenuActions } from "../../jobs2/JobsMenuActions";
import { CustomLoader } from "../../shared/components/loaders/CustomLoader";
import { useDebounce } from "../../shared/hooks/useDebounce";
import { alphaVar, colors } from "../../ui/colors";
import { CardNoJob } from "../components/cardNoJobs";
import { Job } from "../interfaces/job.interfaces";
import { useJobSocketRefetch } from "./hooks/useJobSocketRefetch";
import { useJobsAllQuery } from "./hooks/useJobsAllQuery";
import { JobCardLayout } from "./job-card/JobCardLayout";

type JobsListProps = {
  clientUid?: string | null;
};

type JobFilterBy = "id" | "name";

const JOB_FILTER_PLACEHOLDERS: Record<JobFilterBy, string> = {
  id: "Search by job ID...",
  name: "Search by job name...",
};

const INITIAL_VISIBLE_JOBS = 10;
const LOAD_MORE_JOBS = 10;

export const searchBarPlainStyles = {
  minWidth: 280,
  maxWidth: 360,
  width: "100%",
  "& .MuiOutlinedInput-root": {
    bgcolor: "var(--fx-surface-base, #FFFFFF)",
    borderRadius: "16px",
    minHeight: 36,

    "& fieldset": {
      borderColor: "var(--fx-surface-border, #E2E8F0)",
    },
    "&:hover fieldset": {
      borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
    },
    "&.Mui-focused fieldset": {
      borderColor: colors.orange[400],
    },

    "& .MuiInputBase-input": {
      fontSize: "0.85rem",
      fontWeight: 500,
    },
  },
};

function getJobSearchName(job: Job): string {
  const anyJob = job as any;

  const client = anyJob?.client;
  const clientName = client?.clientCompanyName || `${client?.firstName ?? ""} ${client?.lastName ?? ""}`.trim();
  const coverageArea = anyJob?.coverageArea || anyJob?.quote?.coverageArea || {};
  const eventCoverageAreas = Array.isArray(anyJob?.events) ? anyJob.events.map((event: any) => event?.coverageArea) : [];

  return [
    anyJob?.alias,
    anyJob?.name,
    anyJob?.title,
    anyJob?.type,
    anyJob?.status,
    anyJob?.service?.name,
    anyJob?.typeOfService?.name,
    anyJob?.scopeOfWork?.typeOfService?.name,
    clientName,
    client?.alias,
    client?.address,
    client?.city,
    client?.state,
    client?.country,
    client?.zipCode,
    coverageArea?.name,
    coverageArea?.address,
    coverageArea?.city,
    coverageArea?.state,
    coverageArea?.country,
    coverageArea?.zipCode,
    ...eventCoverageAreas.flatMap((area: any) => [area?.name, area?.address, area?.city, area?.state, area?.country, area?.zipCode]),
  ]
    .filter(Boolean)
    .join(" ");
}

function filterJobsByField(jobs: Job[], filterBy: JobFilterBy, searchValue: string): Job[] {
  const term = sanitizeSearchQuery(searchValue).trim().toLowerCase();
  if (!term) return jobs;

  return jobs.filter((job) => {
    const anyJob = job as any;

    if (filterBy === "id") {
      return String(anyJob?.id ?? "")
        .toLowerCase()
        .includes(term);
    }

    return getJobSearchName(job).toLowerCase().includes(term);
  });
}

function JobSearchBar({
  filterBy,
  onFilterChange,
  searchValue,
  onSearchChange,
  onClear,
  inputRef,
  isFetching,
}: {
  filterBy: JobFilterBy;
  onFilterChange: (event: any) => void;
  searchValue: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
  isFetching?: boolean;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
      <TextField
        inputRef={inputRef}
        size="small"
        placeholder={JOB_FILTER_PLACEHOLDERS[filterBy] ?? "Search..."}
        value={searchValue}
        onChange={onSearchChange}
        sx={searchBarPlainStyles}
        inputProps={{
          autoComplete: "off",
          autoCorrect: "off",
          autoCapitalize: "off",
          spellCheck: "false",
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: colors.gray[400], fontSize: 18, ml: 0.5 }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end" sx={{ gap: 0.5 }}>
              {isFetching ? <CircularProgress size={16} sx={{ color: colors.orange.base }} /> : null}
              {!!searchValue && (
                <Fade in>
                  <IconButton size="small" onClick={onClear} sx={{ p: 0.25, mr: 0.5 }}>
                    <ClearIcon sx={{ fontSize: 16, color: colors.gray[400] }} />
                  </IconButton>
                </Fade>
              )}
            </InputAdornment>
          ),
        }}
      />

      <FormControl size="small">
        <Select
          value={filterBy}
          onChange={onFilterChange}
          sx={{
            height: 36,
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            borderRadius: "12px",
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: "text.secondary",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--fx-surface-border, #E2E8F0)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.orange[400],
            },
          }}
        >
          <MenuItem value="name">Search by Name</MenuItem>
          <MenuItem value="id">Search by ID</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );
}

function EmptyJobsSearchState({ searchValue, onClear }: { searchValue: string; onClear: () => void }) {
  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "calc(90vh - 260px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "calc(100% - 48px)",
          borderRadius: "16px",
          border: `1px solid ${alphaVar(colors.gray[100], 0.55)}`,
          bgcolor: "background.paper",
          px: { xs: 3, sm: 4 },
          py: 4.5,
          textAlign: "center",
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            height: 150,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "visible",
            mb: 1,
          }}
        >
          <RiveByKey riveKey="createjob" boxSize={180} />
        </Box>

        <Typography
          sx={{
            fontSize: "1.15rem",
            fontWeight: 850,
            color: "var(--fx-surface-text, #0F172A)",
            mb: 0.75,
          }}
        >
          No jobs found
        </Typography>

        <Typography
          sx={{
            fontSize: "0.875rem",
            color: "var(--fx-surface-textsecondary, #64748B)",
            lineHeight: 1.5,
            maxWidth: 320,
            mx: "auto",
            mb: 2.5,
          }}
        >
          No jobs match “{searchValue}”. Try another ID or job name.
        </Typography>

        <Box
          component="button"
          type="button"
          onClick={onClear}
          style={{
            border: "none",
            cursor: "pointer",
          }}
          sx={{
            height: 36,
            px: 2.25,
            borderRadius: "999px",
            bgcolor: colors.orange.base,
            color: "#FFFFFF",
            fontSize: "0.8125rem",
            fontWeight: 800,
            boxShadow: "0 8px 18px rgba(234, 88, 12, 0.18)",
            transition: "all 140ms ease",

            "&:hover": {
              bgcolor: colors.orange[600] || colors.orange.base,
              boxShadow: "0 10px 24px rgba(234, 88, 12, 0.24)",
            },
          }}
        >
          Clear search
        </Box>
      </Paper>
    </Box>
  );
}

function JobCardModalHost({ modalKey }: { modalKey?: string | null }) {
  const { modals } = useModals();
  const restoredJobModalKey = modals.find((modal: any) => /^JobCard-\d+$/.test(String(modal?.key)))?.key;
  const activeModalKey = modalKey || restoredJobModalKey;

  if (!activeModalKey) return null;

  return <BaseModalComponent modalLabel={`Job #${String(activeModalKey).replace("JobCard-", "")}`} modalKey={activeModalKey} preferModalContent />;
}

export const JobsList: React.FC<JobsListProps> = ({ clientUid = null }) => {
  console.count("render JobsList");

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [filterBy, setFilterBy] = useState<JobFilterBy>("name");
  const [searchValue, setSearchValue] = useState("");
  const [activeJobModalKey, setActiveJobModalKey] = useState<string | null>(null);
  const [requestedJobCount, setRequestedJobCount] = useState(INITIAL_VISIBLE_JOBS);
  const debouncedSearchValue = useDebounce(searchValue, 350);
  const jobsAllQuery = useJobsAllQuery({ page: 1, pageSize: requestedJobCount, clientUid, search: debouncedSearchValue, searchBy: filterBy });

  const jobItems: Job[] = useMemo(() => (jobsAllQuery.data?.data?.items ?? []) as Job[], [jobsAllQuery.data]);
  const totalJobItems = Number(jobsAllQuery.data?.data?.totalItems ?? jobItems.length);

  const filteredJobItems: Job[] = useMemo(() => filterJobsByField(jobItems, filterBy, searchValue), [jobItems, filterBy, searchValue]);
  const [visibleJobCount, setVisibleJobCount] = useState(INITIAL_VISIBLE_JOBS);
  const visibleFilteredJobItems = useMemo(() => filteredJobItems.slice(0, visibleJobCount), [filteredJobItems, visibleJobCount]);
  const hasMoreFilteredJobs = visibleJobCount < filteredJobItems.length || jobItems.length < totalJobItems;

  useJobSocketRefetch({
    jobItems,
    refetch: jobsAllQuery.refetch,
  });

  const isLoading = jobsAllQuery.isLoading;
  const hasJobs = jobItems.length > 0;
  const hasFilteredJobs = filteredJobItems.length > 0;
  const isSearching = Boolean(searchValue.trim());
  const isSearchPending = jobsAllQuery.isFetching || searchValue.trim() !== debouncedSearchValue.trim();
  const shouldShowSearchAndList = hasJobs || isSearching;

  useEffect(() => {
    console.log("[JobsList perf] query state", {
      isLoading: jobsAllQuery.isLoading,
      isFetching: jobsAllQuery.isFetching,
      isSuccess: jobsAllQuery.isSuccess,
      jobItems: jobItems.length,
      totalJobItems,
      filteredJobItems: filteredJobItems.length,
      visibleJobCount,
      requestedJobCount,
    });
  }, [
    filteredJobItems.length,
    jobItems.length,
    jobsAllQuery.isFetching,
    jobsAllQuery.isLoading,
    jobsAllQuery.isSuccess,
    requestedJobCount,
    totalJobItems,
    visibleJobCount,
  ]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const cleanValue = sanitizeSearchQuery(event.target.value);
    setSearchValue(cleanValue);
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setFilterBy("name");
    searchInputRef.current?.focus();
  };

  const handleFilterChange = (event: any) => {
    setFilterBy(event.target.value);
    setSearchValue("");
    searchInputRef.current?.focus();
  };

  useEffect(() => {
    setVisibleJobCount(INITIAL_VISIBLE_JOBS);
    setRequestedJobCount(INITIAL_VISIBLE_JOBS);
  }, [clientUid, filterBy, debouncedSearchValue]);

  const handleLoadMoreJobs = () => {
    setVisibleJobCount((current) => current + LOAD_MORE_JOBS);
    setRequestedJobCount((current) => current + LOAD_MORE_JOBS);
  };

  const isInitialLoading = isLoading && !jobsAllQuery.data && !isSearching;
  if (isInitialLoading) return <CustomLoader message="Loading..." />;

  return (
    <Container sx={{ p: 2, display: "flex", flexDirection: "column", flexGrow: 1, minHeight: "100%" }}>
      <JobCardModalHost modalKey={activeJobModalKey} />

      {shouldShowSearchAndList ? (
        <Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            sx={{ pb: 2 }}
          >
            <JobSearchBar
              filterBy={filterBy}
              onFilterChange={handleFilterChange}
              searchValue={searchValue}
              onSearchChange={handleSearchChange}
              onClear={handleClearSearch}
              inputRef={searchInputRef}
              isFetching={isSearchPending}
            />

            <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
              <JobMenuActions
                clientUid={clientUid}
                variant="dropdown"
                dropdownLabel="Add New Job"
                dropdownProps={{ fullWidth: false, maxWidth: 240 }}
                hideSeeJobs
              />
            </Box>
          </Stack>

          {isSearchPending && isSearching ? (
            <LinearProgress
              sx={{
                my: 1,
                borderRadius: 1,
                bgcolor: alphaVar(colors.orange.base, 0.15),
                "& .MuiLinearProgress-bar": {
                  bgcolor: colors.orange.base,
                },
              }}
            />
          ) : null}

          {hasFilteredJobs ? (
            <>
              <List sx={{ width: "100%" }}>
                {visibleFilteredJobItems.map((job: Job) => (
                  <JobCardLayout key={String(job.id)} job={job} jobsAllQuery={jobsAllQuery} onJobModalOpened={setActiveJobModalKey} />
                ))}
              </List>

              {hasMoreFilteredJobs && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleLoadMoreJobs}
                    disabled={jobsAllQuery.isFetching}
                    sx={{
                      borderColor: colors.orange[300],
                      color: colors.orange[600] || colors.orange.base,
                      fontWeight: 800,
                      textTransform: "none",
                      "&:hover": {
                        borderColor: colors.orange.base,
                        bgcolor: alphaVar(colors.orange.base, 0.08),
                      },
                    }}
                  >
                    Load more jobs
                  </Button>
                </Box>
              )}
            </>
          ) : !isSearchPending ? (
            <EmptyJobsSearchState searchValue={searchValue} onClear={handleClearSearch} />
          ) : null}
        </Box>
      ) : (
        <CardNoJob
          title="No Jobs Yet"
          description="You don't have any jobs assigned at the moment. Start by creating one from a quote or from scratch."
          actionComponent={<JobMenuActions clientUid={clientUid} variant="dropdown" dropdownLabel="Create Your First Job" hideSeeJobs />}
        />
      )}
    </Container>
  );
};

export default JobsList;
